import { z } from 'zod';
import { L10n, ModuleId, QuestionId, Track, moduleOf } from './common.js';
import { MemberCheckKey } from './session.js';

export const QuestionType = z.enum([
  'single', // one choice
  'multi', // several; banks before v2 could cap the picks
  'scale', // 1–5 with labelled ends
  'band', // one of a few ranges
  'text', // free text, voice input via the native keyboard on mobile
  'pick_explain', // choices plus an optional "say more" line
  'rank', // first one, then order two more
  'contact', // the one form, at I4
  'checkbox', // consent, at I5
  'compound', // several short parts on one screen (C3: band x2 + single)
]);
export type QuestionType = z.infer<typeof QuestionType>;

export const Option = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  label: L10n,
  /** Machine tag: a CapabilityTag on E1, a semantic flag elsewhere (e.g. "specific_incident" on A5). */
  tag: z.string().optional(),
  /** Picking it clears every other pick ("None of these", "Not really worried"). */
  exclusive: z.boolean().optional(),
  /** Numeric midpoint of a band, used for people-hours sizing. null means "hard to estimate". */
  value: z.number().nullable().optional(),
  /** A status that is not a problem ("working well"); never becomes a card in a derived list such as OI1. */
  positive: z.boolean().optional(),
});
export type Option = z.infer<typeof Option>;

export const ShowIfRule = z.object({
  q: QuestionId,
  op: z.enum(['answered', 'includesAny', 'excludesAll', 'equals', 'notEquals']),
  values: z.array(z.string()).optional(),
});
export type ShowIfRule = z.infer<typeof ShowIfRule>;

/**
 * Option overrides for a slice of respondents: by industry (A1 option ids; B3 adds "work orders and
 * maintenance" for manufacturing) or by any answer rule. The first matching variant wins.
 */
export const Variant = z
  .object({
    industries: z.array(z.string()).min(1).optional(),
    when: z.array(ShowIfRule).min(1).optional(),
    /** Replaces the base options entirely. */
    replace: z.array(Option).optional(),
    add: z.array(Option).optional(),
    /** Option ids moved to the front, in this order. */
    order: z.array(z.string()).optional(),
  })
  .refine((v) => v.industries || v.when, 'a variant needs industries or when');
export type Variant = z.infer<typeof Variant>;

/** Options built from the client's own earlier picks (C2 from C1, D2 from D1, I1 from C1 + B3). */
export const OptionsFrom = z.object({
  questions: z.array(QuestionId).min(1),
  /** Cap on the derived list, after de-duplication. */
  max: z.number().int().positive().optional(),
});
export type OptionsFrom = z.infer<typeof OptionsFrom>;

export const ScaleSpec = z.object({
  min: z.literal(1),
  max: z.literal(5),
  minLabel: L10n,
  maxLabel: L10n,
});

export const QuestionPart = z.object({
  id: z.string().regex(/^[a-z]+$/),
  type: z.enum(['band', 'single']),
  label: L10n,
  options: z.array(Option).min(2),
});
export type QuestionPart = z.infer<typeof QuestionPart>;

export const Question = z.object({
  id: QuestionId,
  /** Bumped when the wording or options change, so every answer records the exact version it answered. */
  version: z.number().int().positive(),
  type: QuestionType,
  /** May contain {C2}, substituted with the pain chosen in C2. */
  prompt: L10n,
  /** Used when {C2} cannot be substituted because C2 was skipped. */
  promptIfMissing: L10n.optional(),
  /** Plainer wording for frontline respondents (A3 = frontline). */
  promptFrontline: L10n.optional(),
  help: L10n.optional(),
  /** English intent tag the model sees next to each answer. */
  intent: z.string().min(3),
  /** Analytics grouping and the brief section the answer feeds. */
  dimension: z.string().min(2),
  options: z.array(Option).optional(),
  optionsFrom: OptionsFrom.optional(),
  variants: z.array(Variant).optional(),
  parts: z.array(QuestionPart).optional(),
  scale: ScaleSpec.optional(),
  max: z.number().int().positive().optional(),
  required: z.boolean().default(false),
  /** Engine appends 其他 / Other with a text box to choice questions unless false. */
  allowOther: z.boolean().default(true),
  /** Engine appends 不确定 / Not sure to judgement questions; set true on those. */
  allowUnsure: z.boolean().default(false),
  /** Overrides the 不确定 / Not sure wording (e.g. 不好估 / Hard to estimate). */
  unsureLabel: L10n.optional(),
  /** Optional one-line "say more" box under the options (D1, E3). */
  explain: z.object({ prompt: L10n }).optional(),
  /** Prompt chips above a text box (B4: 谁 Who · 什么事 What …). */
  chips: z.array(L10n).optional(),
  placeholder: L10n.optional(),
  /** Short encouragement under a text box ("两三句就好"). */
  hint: L10n.optional(),
  /** Free-text screens carry the line 请勿填写本人或他人的姓名、公司名称及联系方式. */
  noNamesNote: z.boolean().optional(),
  showIf: z.array(ShowIfRule).optional(),
  /** Questions sharing a screen key render together (A2, A2b and A3 as three chip rows). */
  screen: z.string().optional(),
  /** Counts toward the 60% completeness rule for a full brief. */
  core: z.boolean().default(false),
  /** "optional · cut first": the first to go if pilot timings run past twelve minutes (see hideOptional). */
  optional: z.boolean().optional(),
  /** Consultant-facing note from the bank tables; never shown to clients or sent to the model. */
  notes: z.string().optional(),
});
export type Question = z.infer<typeof Question>;
export type QuestionInput = z.input<typeof Question>;

export const Module = z.object({
  id: ModuleId,
  title: L10n,
  /** One sentence shown after the module, auto-advancing in 1.5 s (after C, F and I). */
  interstitial: L10n.optional(),
  questions: z.array(Question).min(1),
});
export type Module = z.infer<typeof Module>;

/**
 * The question ids the engine and the brief pipeline treat specially. Each track names its own, so nothing
 * outside the bank hard-codes an id.
 */
export const EngineConfig = z.object({
  /** Sets the industry variants (A1; OA1 product category). */
  industryQuestion: QuestionId,
  roleQuestion: QuestionId,
  /** Roles that get `promptFrontline` wording (consulting: frontline staff; overseas: sales reps). */
  plainRoles: z.array(z.string()),
  /** The single worst pain, substituted as {C2} / {OE2} and anchoring the brief. */
  topPainQuestion: QuestionId,
  /** Compound question with `people` and `hours` band parts, multiplied into people-hours. */
  magnitudeQuestion: QuestionId,
  /** Banks published before v2 named the one-sentence complaint here; it is no longer asked or quoted. */
  quoteQuestion: QuestionId.optional(),
  /** Submitting it calls /complete and starts the follow-up generation. */
  openFloorQuestion: QuestionId,
  /** Hidden by a branch but meaning zero, not unknown (overseas: not started → no inquiries, no outreach). */
  zeroWhenHidden: z.array(QuestionId).default([]),
  /** The five 我们听到的是 lines; `template` ({x} = the answer) overrides the default wording for a track. */
  memberCheck: z.array(z.object({ key: MemberCheckKey, source: QuestionId, template: L10n.optional() })).min(1).max(5),
});
export type EngineConfig = z.infer<typeof EngineConfig>;

export const Questionnaire = z
  .object({
    track: Track.default('consulting'),
    version: z.number().int().positive(),
    status: z.enum(['draft', 'published']),
    title: L10n,
    publishedAt: z.string().optional(),
    /** Drop every question marked optional (cut first) without editing them out of the bank. */
    hideOptional: z.boolean().default(false),
    engine: EngineConfig,
    modules: z.array(Module).length(9),
  })
  .superRefine((q, ctx) => {
    const ids = new Set<string>();
    for (const m of q.modules) {
      for (const question of m.questions) {
        if (ids.has(question.id)) ctx.addIssue({ code: 'custom', message: `duplicate question id ${question.id}` });
        ids.add(question.id);
        if (moduleOf(question.id) !== m.id || (q.track === 'overseas') !== question.id.startsWith('O')) {
          ctx.addIssue({ code: 'custom', message: `${question.id} sits in module ${m.id}` });
        }
        const choice = ['single', 'multi', 'band', 'pick_explain'].includes(question.type);
        if (choice && !question.options?.length && !question.optionsFrom) {
          ctx.addIssue({ code: 'custom', message: `${question.id} is a choice question without options` });
        }
        if (question.type === 'compound' && !question.parts?.length) {
          ctx.addIssue({ code: 'custom', message: `${question.id} is compound without parts` });
        }
        if (question.type === 'scale' && !question.scale) {
          ctx.addIssue({ code: 'custom', message: `${question.id} is a scale without labelled ends` });
        }
        const baseIds = new Set<string>();
        for (const o of question.options ?? []) {
          if (baseIds.has(o.id)) ctx.addIssue({ code: 'custom', message: `${question.id} has duplicate option ${o.id}` });
          baseIds.add(o.id);
        }
        for (const v of question.variants ?? []) {
          for (const o of v.add ?? []) {
            if (baseIds.has(o.id)) ctx.addIssue({ code: 'custom', message: `${question.id} variant re-adds option ${o.id}` });
          }
        }
      }
    }
    for (const m of q.modules) {
      for (const question of m.questions) {
        for (const rule of question.showIf ?? []) {
          if (!ids.has(rule.q)) ctx.addIssue({ code: 'custom', message: `${question.id} showIf refers to unknown ${rule.q}` });
        }
        for (const src of question.optionsFrom?.questions ?? []) {
          if (!ids.has(src)) ctx.addIssue({ code: 'custom', message: `${question.id} optionsFrom refers to unknown ${src}` });
        }
      }
    }
    const e = q.engine;
    for (const id of [e.industryQuestion, e.roleQuestion, e.topPainQuestion, e.magnitudeQuestion, ...(e.quoteQuestion ? [e.quoteQuestion] : []), e.openFloorQuestion, ...e.zeroWhenHidden, ...e.memberCheck.map((m) => m.source)]) {
      if (!ids.has(id)) ctx.addIssue({ code: 'custom', message: `engine config refers to unknown ${id}` });
    }
  });
export type Questionnaire = z.infer<typeof Questionnaire>;
export type QuestionnaireInput = z.input<typeof Questionnaire>;

export const allQuestions = (q: Questionnaire): Question[] => q.modules.flatMap((m) => m.questions);

export const findQuestion = (q: Questionnaire, id: string): Question | undefined =>
  allQuestions(q).find((question) => question.id === id);
